// lembrar de impedir nomes repetidos.

import axios from "axios";
import { SignJWT } from "jose";
// import { v4 as uuidv4 } from "uuid";

const sheetId = "14K_4OMkIopQgXqW1bBZKrAW2c3Glg8G1Rdd6fZZwqDQ";
const serviceAccountEmail = "games-hab@compartilhar-planilhas-443921.iam.gserviceaccount.com";
let privateKey = "";

/** 
 * Carrega a chave privada a partir do arquivo de credenciais.
 */
async function loadPrivateKey() {
  const response = await fetch("/assets/keys/credentials.json");
  const json = await response.json();
  privateKey = json.private_key;
  return privateKey;
}

/** 
 * Gera um JWT e solicita um token de acesso à API do Google Sheets.
 */
async function generateJwt() {
  if (!privateKey) {
    await loadPrivateKey();
  }
  const pem = privateKey.replace(/-----.*-----/g, "").replace(/\n/g, "");
  const privateKeyBuffer = Uint8Array.from(atob(pem), (c) => c.charCodeAt(0)).buffer;
  const cryptoKey = await crypto.subtle.importKey(
    "pkcs8",
    privateKeyBuffer,
    { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
    true,
    ["sign"]
  );
  const jwt = await new SignJWT({
    iss: serviceAccountEmail,
    scope: "https://www.googleapis.com/auth/spreadsheets",
    aud: "https://oauth2.googleapis.com/token",
  })
    .setProtectedHeader({ alg: "RS256" })
    .setIssuedAt()
    .setExpirationTime("1h")
    .sign(cryptoKey);
  const response = await axios.post("https://oauth2.googleapis.com/token", {
    grant_type: "urn:ietf:params:oauth:grant-type:jwt-bearer",
    assertion: jwt,
  });
  return response.data.access_token;
}

/** 
 * Busca as categorias da planilha.
 */
async function fetchCategories() {
  const token = await generateJwt();
  const range = "GRUPOS!A2:B";
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.values.map((row, index) => ({
    id: index + 1,
    title: row[0],
  }));
}

/**
 * Salva os detalhes do treinamento na planilha.
 * @param {string} trainingName - Nome da aula/treinamento.
 * @param {string} categoryName - Nome da categoria selecionada.
 * @param {string} sessionKey - Chave de sessão única.
 */
async function saveTrainingDetails(trainingName, categoryName, sessionKey) {
  const token = await generateJwt();
  const range = "PARTICIPANTES!A:D";
  const currentDate = new Date().toLocaleString();
  // Colunas: [Training Name, Category Name, Data/Hora, SessionKey]
  const data = { values: [[trainingName, categoryName, currentDate, sessionKey]] };
  await axios.post(
    `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}:append?valueInputOption=USER_ENTERED`,
    data,
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

/**
 * Verifica os participantes do treinamento a partir do sessionKey.
 * Se o nome do participante for fornecido, filtra para retornar apenas o registro daquele participante.
 * @param {string} sessionKey - Chave de sessão única para filtrar os participantes.
 * @param {string} [nomeParticipante] - (Opcional) Nome do participante para filtrar.
 * @returns {Promise<{ currentParticipants: number, participants: Array }>}
 */
async function checkParticipants(sessionKey, nomeParticipante = null) {
  const token = await generateJwt();
  const range = "PARTICIPANTES!A:I";
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const allRows = response.data.values;
  const dataRows = allRows.slice(1);
  
  // Filtra os registros pelo sessionKey
  let filtered = dataRows.filter((row) => row[3] === sessionKey);
  
  // Se um nome de participante for fornecido, filtra para aquele nome
  if (nomeParticipante) {
    filtered = filtered.filter((row) => row[0] === nomeParticipante);
  }
  
  const currentParticipants = filtered.length;
  const participants = filtered.map((row) => ({
    name: row[0],
    status: row[4] ? row[4] : "Pendente",
    nota: row[5] ? row[5] : 0,
    acertos: row[6] ? row[6] : 0,
    erros: row[7] ? row[7] : 0,
  }));
  return { currentParticipants, participants };
}

/**
 * Libera o quiz atualizando o status dos participantes.
 * @param {string} sessionKey - Chave de sessão única para filtrar os participantes.
 */
async function liberateQuiz(sessionKey) {
  const token = await generateJwt();
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/PARTICIPANTES!A:E`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const rows = response.data.values || [];
  const updates = [];
  rows.forEach((row, index) => {
    if (row[3] === sessionKey) {
      updates.push({
        range: `PARTICIPANTES!E${index + 1}`,
        values: [["LIBERADO"]],
      });
    }
  });
  await axios.post(
    `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values:batchUpdate`,
    { data: updates, valueInputOption: "USER_ENTERED" },
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

/**
 * Busca as perguntas da planilha para uma determinada categoria.
 * @param {string} categoryName - Nome da categoria.
 * @returns {Promise<Array>} Lista de perguntas.
 */
async function fetchQuestions(categoryName) {
  const token = await generateJwt();
  const range = "PERGUNTAS!A2:I";
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const rows = response.data.values;
  const questions = rows.filter((row) => row[0] === categoryName)
    .map((row, index) => {
      const id = index + 2; // Considerando que a primeira linha é cabeçalho
      const tipo = row[2] ? row[2].toUpperCase().trim() : "";
      let options = [];
      if (tipo === "BOLEANO") {
        options = ["VERDADE", "FALSO"];
      } else {
        options = row.slice(4, 9).filter((opt) => opt && opt.trim() !== "");
      }
      return {
        id,
        question: row[1].trim(),
        correctAnswer: row[3].trim(),
        options,
        tipo,
      };
    });
    console.log('PERGUNTAS: ',questions);
  return questions;
}

/**
 * Salva as respostas do quiz na planilha.
 * @param {string} playerName - Nome do jogador.
 * @param {string} categoryName - Nome da categoria.
 * @param {string} sessionKey - Chave de sessão do participante.
 * @param {Array} responses - Lista de respostas.
 */
async function saveAnswers(playerName, categoryName, sessionKey, responses) {
  const token = await generateJwt();
  // Inclui a sessionKey como coluna adicional após a data/hora
  const rowsData = responses.map((resp) => [
    playerName,
    categoryName,
    resp.questionId,
    resp.question,
    resp.chosen,
    resp.correct,
    resp.result,
    new Date().toLocaleString(),
    sessionKey // grava a sessionKey conforme gerada
  ]);
  // Atualize o range para refletir 9 colunas (A a I)
  const range = "RESPOSTAS!A:I";
  const data = { values: rowsData };
  await axios.post(
    `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}:append?valueInputOption=USER_ENTERED`,
    data,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  // Após salvar as respostas, atualiza o status do participante para "CONCLUÍDO"
  await updateStudentStatus(playerName, sessionKey);
}

/**
 * Atualiza o status do participante para "CONCLUÍDO".
 * @param {string} playerName - Nome do jogador.
 * @param {string} sessionKey - Chave de sessão do participante.
 */
async function updateStudentStatus(playerName, sessionKey) {
  const token = await generateJwt();
  const range = "PARTICIPANTES!A:E";
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const rows = response.data.values;
  const dataRows = rows.slice(1);
  const idx = dataRows.findIndex(
    (row) => row[0] === playerName && row[3] === sessionKey
  );
  if (idx !== -1) {
    // O número da linha real é idx + 2 (por causa do cabeçalho)
    const actualRow = idx + 2;
    const updateRange = `PARTICIPANTES!E${actualRow}`;
    const updateData = { values: [["CONCLUÍDO"]] };
    await axios.put(
      `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${updateRange}?valueInputOption=USER_ENTERED`,
      updateData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } else {
    console.error("Registro do participante não encontrado para atualização.");
  }
}

/**
 * Registra um jogador na planilha.
 * @param {string} playerName - Nome do jogador.
 * @param {string} categoryName - Nome da categoria.
 * @param {string} sessionKey - Chave de sessão.
 * @param {number} maxParticipants - Número máximo de participantes permitidos.
 * @returns {Promise<string>} Retorna a data e hora do registro se bem-sucedido.
 */
async function registerPlayer(playerName, categoryName, sessionKey, maxParticipants) {
  if (!playerName.trim()) {
    throw new Error("Por favor, digite seu nome ou apelido.");
  }
  const currentDate = new Date().toLocaleString();
  const token = await generateJwt();
  const range = "PARTICIPANTES!A:D";

  // Verifica os participantes já registrados para a sessão
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const rows = response.data.values || [];
  const dataRows = rows.slice(1);
  const participantsList = dataRows.filter((row) => row[3] === sessionKey);

  if (participantsList.length >= maxParticipants) {
    throw new Error(`O limite de ${maxParticipants} participantes já foi atingido.`);
  }

  // Adiciona o participante com as colunas: Nome, Categoria, Data/Hora, SessionKey
  const data = { values: [[playerName, categoryName, currentDate, sessionKey]] };
  await axios.post(
    `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}:append?valueInputOption=USER_ENTERED`,
    data,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return currentDate;
}

/**
 * Verifica o status do quiz.
 * @param {string} sessionKey - Chave de sessão.
 * @returns {Promise<boolean>} Retorna true se o quiz estiver liberado.
 */
async function checkQuizStatus(sessionKey) {
  const token = await generateJwt();
  const range = "PARTICIPANTES!D:E";
  const response = await axios.get(
    `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const rows = response.data.values || [];
  const sessionStatus = rows.find(
    (row) => row[0] === sessionKey && row[1] === "LIBERADO"
  );
  return !!sessionStatus;
}

export {
  loadPrivateKey,
  generateJwt,
  fetchCategories,
  saveTrainingDetails,
  checkParticipants,
  liberateQuiz,
  fetchQuestions,
  saveAnswers,
  updateStudentStatus,
  registerPlayer,
  checkQuizStatus
};
